// src/components/settings/PlatformsTab.jsx - Complete optimized version
import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { settingsAPI } from '../../services/api';
import { Switch } from '@headlessui/react';
import LinkedInOnboardingWizard from "./LinkedInOnboardingWizard.jsx"; // Add this import
import toast from 'react-hot-toast';
import {
    LinkedInSetupStatus,
    TwitterSetupStatus,
    InstagramSetupStatus,
    BlueSkySetupStatus,
    DiscordSetupStatus,
    FacebookSetupStatus
} from "./PlatformSetupStatus.jsx";
import {
    SparklesIcon,
    BoltIcon,
    CpuChipIcon
} from '@heroicons/react/24/outline';

import DiscordOnboardingWizard from './DiscordOnboardingWizard';
import TwitterOnboardingWizard from './TwitterOnboardingWizard';
import BlueskyOnboardingWizard from './BlueskyOnboardingWizard';
import MetaPlatformsSetup from './MetaPlatformsSetup';

const platforms = [
    {
        id: 'linkedin',
        name: 'LinkedIn',
        icon: '💼',
        description: 'Professional networking and B2B content',
        hasSubOptions: true,
        hasWizard: true, // Add this flag
        subOptions: [
            { id: 'personal_only', name: 'Personal Profile Only', description: 'Post to your personal LinkedIn profile' },
            { id: 'organization_only', name: 'Company Page Only', description: 'Post to your company LinkedIn page' },
            { id: 'both', name: 'Both Personal & Company', description: 'Post to both profiles' }
        ]
    },
    {
        id: 'twitter',
        name: 'Twitter / X',
        icon: '🐦',
        description: 'Microblogging and real-time updates',
        hasWizard: true  // Add this line
    },
    {
        id: 'instagram',
        name: 'Instagram',
        icon: '📸',
        description: 'Visual content and stories',
        hasWizard: true
    },
    {
        id: 'facebook',
        name: 'Facebook',
        icon: '👤',
        description: 'Visual content and stories',
        hasWizard: true
    },
    {
        id: 'bluesky',
        name: 'Bluesky',
        icon: '🦋',
        description: 'Decentralized social network',
        hasWizard: true  // Add this line
    }
];

// Memoized Platform Card Component
const PlatformCard = React.memo(({
                                     platform,
                                     platformData,
                                     isEnabled,
                                     isLoading,
                                     onToggle,
                                     onLinkedInModeChange,
                                     onOpenWizard // Add this prop
                                 }) => {
    const handleToggle = useCallback((enabled) => {
        onToggle(platform.id, enabled);
    }, [platform.id, onToggle]);


    const shouldDebug = process.env.NODE_ENV === 'development';

    // if (shouldDebug) {
    //     console.log(`Platform ${platform.id} final check:`, {
    //         platformData,
    //         enabled: isEnabled,
    //         rawEnabled: platformData.enabled,
    //         typeOfEnabled: typeof platformData.enabled
    //     });
    // }

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="text-2xl">{platform.icon}</div>
                    <div>
                        <h4 className="text-lg font-medium text-gray-900">{platform.name}</h4>
                        <p className="text-sm text-gray-600">{platform.description}</p>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    {platform.hasWizard && (
                        <button
                            onClick={() => onOpenWizard(platform.id)}
                            className="inline-flex items-center px-3 py-1 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                            🪄 Setup Wizard
                        </button>
                    )}

                    {isLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}

                    <Switch
                        checked={isEnabled}
                        onChange={handleToggle}
                        disabled={isLoading}
                        className={`${
                            isEnabled ? 'bg-blue-600' : 'bg-gray-200'
                        } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        <span
                            className={`${
                                isEnabled ? 'translate-x-5' : 'translate-x-0'
                            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                        />
                    </Switch>
                </div>
            </div>

            {/* LinkedIn sub-options */}
            {platform.id === 'linkedin' && isEnabled && platform.hasSubOptions && (
                <div className="mt-4 pl-12 space-y-3">
                    <p className="text-sm font-medium text-gray-700">Posting Mode:</p>
                    {platform.subOptions.map((option) => {
                        const currentMode = platformData.postingMode || 'personal_only';
                        return (
                            <label key={option.id} className="flex items-start cursor-pointer">
                                <input
                                    type="radio"
                                    name="linkedinMode"
                                    value={option.id}
                                    checked={currentMode === option.id}
                                    onChange={() => onLinkedInModeChange(option.id)}
                                    className="mt-1 mr-3"
                                />
                                <div>
                                    <div className="text-sm font-medium text-gray-700">{option.name}</div>
                                    <div className="text-sm text-gray-500">{option.description}</div>
                                </div>
                            </label>
                        );
                    })}
                </div>
            )}

            {/* Platform-specific Setup Status Components */}
            {platform.id === 'linkedin' && isEnabled && <LinkedInSetupStatus />}
            {platform.id === 'twitter' && isEnabled && <TwitterSetupStatus />}
            {platform.id === 'instagram' && isEnabled && <InstagramSetupStatus />}
            {platform.id === 'facebook' && isEnabled && <FacebookSetupStatus />}
            {platform.id === 'bluesky' && isEnabled && <BlueSkySetupStatus />}
        </div>
    );
});

PlatformCard.displayName = 'PlatformCard';

// Main PlatformsTab Component
function PlatformsTab() {
    const queryClient = useQueryClient();
    const [showLinkedInWizard, setShowLinkedInWizard] = useState(false);
    const [showMetaSetup, setShowMetaSetup] = useState(false);
    const [showSetupGuide, setShowSetupGuide] = useState(true);
    const [showStatusOverview, setShowStatusOverview] = useState(true);
    const [showTwitterWizard, setShowTwitterWizard] = useState(false);
    const [showBlueskyWizard, setShowBlueskyWizard] = useState(false);
    const [showInstagramWizard, setShowInstagramWizard] = useState(false);  // Add this
    const [showFacebookWizard, setShowFacebookWizard] = useState(false);    // Add this
    const [showDiscordWizard, setShowDiscordWizard] = useState(false);

    const handleTwitterWizardComplete = () => {
        setShowTwitterWizard(false);
        toast.success('Twitter setup completed!');
        queryClient.invalidateQueries('platformSettings');
    };

    const handleTwitterWizardClose = () => {
        setShowTwitterWizard(false);
    };

    const handleBlueskyWizardComplete = () => {
        setShowBlueskyWizard(false);
        toast.success('Bluesky setup completed!');
        queryClient.invalidateQueries('platformSettings');
    };

    const handleBlueskyWizardClose = () => {
        setShowBlueskyWizard(false);
    };

    const handleInstagramWizardComplete = () => {
        setShowInstagramWizard(false);
        toast.success('Instagram setup completed!');
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('instagramValidation');
    };

    const handleInstagramWizardClose = () => {
        setShowInstagramWizard(false);
    };

    const handleFacebookWizardComplete = () => {
        setShowFacebookWizard(false);
        toast.success('Facebook setup completed!');
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('facebookValidation');
    };

    const handleFacebookWizardClose = () => {
        setShowFacebookWizard(false);
    };

    const handleDiscordWizardComplete = () => {
        setShowDiscordWizard(false);
        toast.success('Discord setup completed!');
        queryClient.invalidateQueries('platformSettings');
    };

    const handleDiscordWizardClose = () => {
        setShowDiscordWizard(false);
    };

    const handleOpenWizard = (platformId) => {
        switch(platformId) {
            case 'linkedin':
                setShowLinkedInWizard(true);
                break;
            case 'twitter':
                setShowTwitterWizard(true);
                break;
            case 'bluesky':
                setShowBlueskyWizard(true);
                break;
            case 'instagram':
                setShowInstagramWizard(true);
                break;
            case 'facebook':
                setShowFacebookWizard(true);
                break;
            case 'discord':
                setShowDiscordWizard(true);
                break;
            default:
                console.warn(`No wizard available for platform: ${platformId}`);
        }
    };

    const handleLinkedInWizardComplete = () => {
        setShowLinkedInWizard(false);
        toast.success('LinkedIn setup completed! You can now post to LinkedIn.');

        // Refresh platform settings to show updated status
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries('linkedinValidation');
    };

    const handleLinkedInWizardClose = () => {
        setShowLinkedInWizard(false);
    };

    const handleMetaWizardOpen = () => {
        setShowMetaSetup(true);
    };

    const handleMetaWizardComplete = () => {
        setShowMetaSetup(false);
        toast.success('Meta platforms setup completed!');

        // Refresh platform settings to show updated status
        queryClient.invalidateQueries('platformSettings');
        queryClient.invalidateQueries(['instagramValidation', 'facebookValidation']);
    };

    const handleMetaWizardClose = () => {
        setShowMetaSetup(false);
    };

    // Optimized query with performance improvements
    const { data: settings, isLoading, error } = useQuery(
        'platformSettings',
        settingsAPI.getPlatforms,
        {
            retry: 1,
            staleTime: 30000, // 30 seconds - prevents excessive refetching
            cacheTime: 300000, // 5 minutes
            refetchOnWindowFocus: false, // Prevent refetch on window focus
            refetchOnReconnect: false, // Prevent refetch on reconnect
            onError: (error) => {
                console.error('Platform settings error:', error);
                if (error.response?.status === 401) {
                    toast.error('Please log in again');
                }
            }
        }
    );

    // Add this right after the existing settings query
    // Query for AI provider
    const { data: aiProviderData } = useQuery(
        'aiProvider',
        () => fetch('/api/settings/ai-provider', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
            }
        }).then(res => res.json()),
        {
            staleTime: 30000,
            cacheTime: 300000,
        }
    );

    const { data: credentialsData } = useQuery(
        'userCredentials',
        () => fetch('/api/settings/credentials', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
            }
        }).then(res => res.json()),
        {
            staleTime: 30000,
            cacheTime: 300000,
        }
    );

    // Mutation for updating AI provider
    const updateAIProviderMutation = useMutation(
        async (provider) => {
            // Validate credentials before switching
            const credentials = credentialsData?.data || {};

            if (provider === 'openai' && !credentials.OPENAI_API_KEY) {
                throw new Error('OpenAI API key required. Please add your OpenAI API key in the Credentials tab first.');
            }

            if (provider === 'gemini' && !credentials.GEMINI_API_KEY) {
                throw new Error('Gemini API key required. Please add your Gemini API key in the Credentials tab first.');
            }

            const response = await fetch('/api/settings/ai-provider', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
                },
                body: JSON.stringify({ provider })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to update AI provider');
            }

            return response.json();
        },
        {
            onSuccess: (data, provider) => {
                queryClient.invalidateQueries('aiProvider');
                const providerName = provider === 'gemini' ? 'Gemini' : 'OpenAI';
                toast.success(`Successfully switched to ${providerName}!`);
            },
            onError: (error) => {
                toast.error(error.message);
            }
        }
    );

    // Optimized mutation with better cache handling
    const updateMutation = useMutation(settingsAPI.savePlatforms, {
        onMutate: (variables) => {
            console.log('🔄 MUTATION STARTING with variables:', variables);
        },
        onSuccess: (response, variables) => {
            console.log('✅ MUTATION SUCCESS');
            console.log('Response structure:', response);
            console.log('Response data:', response.data);

            // More targeted cache updates
            queryClient.setQueryData('platformSettings', response);

            // Only invalidate specific validation queries that might be affected
            const platformsUpdated = Object.keys(variables.platforms || {});
            platformsUpdated.forEach(platform => {
                queryClient.invalidateQueries(`${platform}Validation`);
            });

            toast.success('Platform settings saved!');
        },
        onError: (error, variables) => {
            console.error('❌ MUTATION ERROR:', error);
            console.error('- Variables:', variables);
            console.error('- Error response:', error.response?.data);

            toast.error(error.response?.data?.message || 'Failed to save settings');
        }
    });

    // Memoized handlers to prevent unnecessary re-renders
    const handleTogglePlatform = useCallback(async (platformId, enabled) => {
        console.log('🚀 TOGGLE INITIATED:', { platformId, enabled });

        const currentSettings = settings?.data;
        let currentPlatforms = {};

        if (currentSettings?.data?.platforms) {
            currentPlatforms = currentSettings.data.platforms;
        } else if (currentSettings?.platforms) {
            currentPlatforms = currentSettings.platforms;
        }

        console.log('📊 BEFORE TOGGLE:');
        console.log('- Current platforms:', JSON.stringify(currentPlatforms, null, 2));
        console.log(`- ${platformId} current state:`, currentPlatforms[platformId]);

        // Create updated platforms object
        const updatedPlatforms = {
            ...currentPlatforms,
            [platformId]: {
                ...currentPlatforms[platformId],
                enabled: enabled
            }
        };

        console.log('📤 SENDING TO BACKEND:');
        console.log('- Updated platforms:', JSON.stringify(updatedPlatforms, null, 2));

        try {
            console.log('🔄 Starting mutation...');
            const result = await updateMutation.mutateAsync({ platforms: updatedPlatforms });
            console.log('✅ Mutation result:', result);
            console.log('✅ Platform toggle completed successfully');
        } catch (error) {
            console.error('❌ Platform toggle mutation failed:', error);
            toast.error(`Failed to update ${platformId}: ${error.message}`);
        }
    }, [settings?.data, updateMutation]);

    // Memoized LinkedIn mode change handler
    const handleLinkedInModeChange = useCallback(async (mode) => {
        const currentSettings = settings?.data;
        let currentPlatforms = {};

        if (currentSettings?.data?.platforms) {
            currentPlatforms = currentSettings.data.platforms;
        } else if (currentSettings?.platforms) {
            currentPlatforms = currentSettings.platforms;
        }

        const updatedPlatforms = {
            ...currentPlatforms,
            linkedin: {
                ...currentPlatforms.linkedin,
                postingMode: mode
            }
        };

        try {
            await updateMutation.mutateAsync({ platforms: updatedPlatforms });
        } catch (error) {
            console.error('LinkedIn mode change failed:', error);
        }
    }, [settings?.data, updateMutation]);

    // Helper function to check if provider can be selected
    const canSelectProvider = useCallback((provider) => {
        const credentials = credentialsData?.data || {};

        if (provider === 'openai') {
            return !!credentials.OPENAI_API_KEY;
        }

        if (provider === 'gemini') {
            return !!credentials.GEMINI_API_KEY;
        }

        return false;
    }, [credentialsData]);

    // Memoized platform data extraction
    const platformsData = useMemo(() => {
        const currentSettings = settings?.data;

        if (currentSettings?.data?.platforms) {
            return currentSettings.data.platforms;
        } else if (currentSettings?.platforms) {
            return currentSettings.platforms;
        } else {
            console.warn('No platforms data found, using empty object');
            return {};
        }
    }, [settings?.data]);

    // Conditional debug logging
    const shouldDebug = process.env.NODE_ENV === 'development';

    // if (shouldDebug) {
    //     console.log('🔍 FINAL PLATFORM DATA DEBUG:', {
    //         rawResponse: settings,
    //         currentSettings: settings?.data,
    //         platformsData,
    //         platformsDataKeys: Object.keys(platformsData),
    //         isUpdateLoading: updateMutation.isLoading
    //     });
    // }

    if (isLoading) {
        return <div className="animate-pulse">Loading platform settings...</div>;
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-600">
                    Failed to load platform settings: {error.message}
                </div>
                <button
                    onClick={() => queryClient.invalidateQueries('platformSettings')}
                    className="mt-2 text-sm text-red-600 hover:text-red-500"
                >
                    Try again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center">
                        <span className="text-2xl mr-3">🚀</span>
                        Platform Setup Guide
                    </h3>
                    <button
                        onClick={() => setShowSetupGuide(!showSetupGuide)}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200 transition-colors"
                    >
                        {showSetupGuide ? (
                            <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                Hide Guide
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                Show Guide
                            </>
                        )}
                    </button>
                </div>

                {showSetupGuide && (
                    <>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Step 1: Credentials */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                                1
                            </div>
                            <h4 className="font-medium text-gray-900">Set Up API Keys</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Configure your platform credentials in the Credentials tab first.
                        </p>
                        <a
                            href="#credentials"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
                        >
                            Go to Credentials →
                        </a>
                    </div>

                    {/* Step 2: Enable Platforms */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                                2
                            </div>
                            <h4 className="font-medium text-gray-900">Enable Platforms</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Toggle on the platforms you want to use below.
                        </p>
                        <span className="text-sm text-gray-500">👇 See platforms below</span>
                    </div>

                    {/* Step 3: Test Connection */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                                3
                            </div>
                            <h4 className="font-medium text-gray-900">Test Connections</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Use the test buttons to verify your setup is working.
                        </p>
                        <span className="text-sm text-gray-500">✅ Test each platform</span>
                    </div>

                    {/* Step 4: Start Posting */}
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex items-center mb-3">
                            <div className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                                4
                            </div>
                            <h4 className="font-medium text-gray-900">Generate Posts</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            Return to Dashboard to create your first AI-powered post.
                        </p>
                        <a
                            href="/dashboard"
                            className="inline-flex items-center text-sm text-orange-600 hover:text-orange-500"
                        >
                            Go to Dashboard →
                        </a>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-6 pt-4 border-t border-blue-200">
                    <h4 className="font-medium text-gray-900 mb-3">Quick Actions:</h4>
                    <div className="flex flex-wrap gap-2">
                        <a
                            href="#onboarding"
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200"
                        >
                            📚 Full Setup Guide
                        </a>
                        <a
                            href="#credentials"
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                        >
                            🔑 Add API Keys
                        </a>
                        <button
                            onClick={() => handleOpenWizard('linkedin')}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200"
                        >
                            💼 LinkedIn Wizard
                        </button>

                        <button
                            onClick={handleMetaWizardOpen}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                        >
                            📱 Meta Platforms Setup
                        </button>

                        <button
                            onClick={() => handleOpenWizard('twitter')}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                        >
                            🐦 Twitter Wizard
                        </button>

                        <button
                            onClick={() => handleOpenWizard('bluesky')}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                        >
                            🦋 BlueSky Wizard
                        </button>

                        <button
                            onClick={() => handleOpenWizard('discord')}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                        >
                            🔔 Discord Wizard
                        </button>
                    </div>
                </div>
                    </>
                )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900">Setup Status Overview</h4>
                    <button
                        onClick={() => setShowStatusOverview(!showStatusOverview)}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        {showStatusOverview ? (
                            <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                                Hide Status
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                Show Status
                            </>
                        )}
                    </button>
                </div>

                {showStatusOverview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {platforms.map((platform) => {
                        const platformData = platformsData[platform.id] || {};
                        const isEnabled = Boolean(platformData.enabled);
                        const hasCredentials = true; // You'll need to check actual credentials

                        return (
                            <div key={platform.id} className="text-center">
                                <div className="text-2xl mb-1">{platform.icon}</div>
                                <div className="text-sm font-medium text-gray-900">{platform.name}</div>
                                <div className={`text-xs mt-1 ${
                                    isEnabled && hasCredentials
                                        ? 'text-green-600'
                                        : isEnabled
                                            ? 'text-yellow-600'
                                            : 'text-gray-400'
                                }`}>
                                    {isEnabled && hasCredentials ? '✅ Ready' :
                                        isEnabled ? '⚠️ Setup needed' : '⚪ Disabled'}
                                </div>
                            </div>
                        );
                    })}
                </div>
                )}
            </div>

            {/* AI Provider Selection Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <CpuChipIcon className="h-6 w-6 text-purple-600" />
                        <div>
                            <h3 className="text-lg font-medium text-gray-900">AI Text Generation Provider</h3>
                            <p className="text-sm text-gray-600">Choose which AI service to use for generating post content</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Gemini Option */}
                    <div
                        className={`relative rounded-lg border-2 p-4 transition-all ${
                            aiProviderData?.data?.aiProvider === 'gemini'
                                ? 'border-purple-500 bg-purple-50'
                                : canSelectProvider('gemini')
                                    ? 'border-gray-200 hover:border-purple-300 cursor-pointer'
                                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                        }`}
                        onClick={() => {
                            if (canSelectProvider('gemini')) {
                                updateAIProviderMutation.mutate('gemini');
                            } else {
                                toast.error('Please add your Gemini API key in the Credentials tab first.');
                                // Optionally navigate to credentials tab
                                window.location.hash = '#credentials';
                            }
                        }}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <SparklesIcon className={`h-6 w-6 ${canSelectProvider('gemini') ? 'text-purple-600' : 'text-gray-400'}`} />
                            </div>
                            <div className="flex-grow">
                                <div className="flex items-center space-x-2">
                                    <h4 className="text-base font-medium text-gray-900">Google Gemini</h4>
                                    {!canSelectProvider('gemini') && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            API Key Required
                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                    Latest Gemini 2.0 Flash model with advanced reasoning and creativity
                                </p>
                                <div className="flex items-center mt-2 space-x-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Recommended
                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Fast
                    </span>
                                    {canSelectProvider('gemini') && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Configured
                        </span>
                                    )}
                                </div>
                            </div>
                            {aiProviderData?.data?.aiProvider === 'gemini' && canSelectProvider('gemini') && (
                                <div className="flex-shrink-0">
                                    <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* OpenAI Option */}
                    <div
                        className={`relative rounded-lg border-2 p-4 transition-all ${
                            aiProviderData?.data?.aiProvider === 'openai'
                                ? 'border-blue-500 bg-blue-50'
                                : canSelectProvider('openai')
                                    ? 'border-gray-200 hover:border-blue-300 cursor-pointer'
                                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                        }`}
                        onClick={() => {
                            if (canSelectProvider('openai')) {
                                updateAIProviderMutation.mutate('openai');
                            } else {
                                toast.error('Please add your OpenAI API key in the Credentials tab first.');
                                // Optionally navigate to credentials tab
                                window.location.hash = '#credentials';
                            }
                        }}
                    >
                        <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                                <BoltIcon className={`h-6 w-6 ${canSelectProvider('openai') ? 'text-blue-600' : 'text-gray-400'}`} />
                            </div>
                            <div className="flex-grow">
                                <div className="flex items-center space-x-2">
                                    <h4 className="text-base font-medium text-gray-900">OpenAI GPT</h4>
                                    {!canSelectProvider('openai') && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            API Key Required
                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                    Proven GPT models with excellent content generation capabilities
                                </p>
                                <div className="flex items-center mt-2 space-x-4">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Alternative
                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Reliable
                    </span>
                                    {canSelectProvider('openai') && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ Configured
                        </span>
                                    )}
                                </div>
                            </div>
                            {aiProviderData?.data?.aiProvider === 'openai' && canSelectProvider('openai') && (
                                <div className="flex-shrink-0">
                                    <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Current Status */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">Current AI Provider:</span>
                        <span className="font-medium text-gray-900">
            {aiProviderData?.data?.aiProvider === 'gemini' ? '🤖 Google Gemini' : '⚡ OpenAI GPT'}
        </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                        This setting affects all new post generation. Make sure you have the corresponding API key configured in the Credentials tab.
                    </p>

                    {/* Warning if current provider doesn't have valid credentials */}
                    {((aiProviderData?.data?.aiProvider === 'openai' && !canSelectProvider('openai')) ||
                        (aiProviderData?.data?.aiProvider === 'gemini' && !canSelectProvider('gemini'))) && (
                        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                            <div className="flex items-center">
                                <svg className="w-4 h-4 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs text-yellow-800">
                    Warning: Current provider API key not found. Please add it in
                    <button
                        onClick={() => window.location.hash = '#credentials'}
                        className="ml-1 text-yellow-800 underline hover:text-yellow-900"
                    >
                        Credentials
                    </button>
                </span>
                            </div>
                        </div>
                    )}
                </div>

                {updateAIProviderMutation.isLoading && (
                    <div className="mt-4 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                        <span className="ml-2 text-sm text-gray-600">Updating AI provider...</span>
                    </div>
                )}
            </div>

            <div>
                <h3 className="text-lg font-medium text-gray-900">Social Media Platforms</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Enable the platforms where you want to publish your content
                </p>
            </div>

            <div className="space-y-4">
                {platforms.map((platform) => {
                    const platformData = platformsData[platform.id] || {};
                    const isEnabled = Boolean(platformData.enabled);

                    return (
                        <PlatformCard
                            key={platform.id}
                            platform={platform}
                            platformData={platformData}
                            isEnabled={isEnabled}
                            isLoading={updateMutation.isLoading}
                            onToggle={handleTogglePlatform}
                            onLinkedInModeChange={handleLinkedInModeChange}
                            onOpenWizard={handleOpenWizard}
                        />
                    );
                })}
            </div>

            {/* Discord Notifications Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                        <div className="text-2xl">🔔</div>
                        <div>
                            <h4 className="text-lg font-medium text-gray-900">Discord Notifications</h4>
                            <p className="text-sm text-gray-600">Get notified in Discord when posts are published</p>
                        </div>
                    </div>
                    <button
                        onClick={() => handleOpenWizard('discord')}
                        className="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100"
                        >
                        🪄 Setup Discord
                    </button>
                </div>

                <DiscordSetupStatus />
            </div>



            {/* Pro Tips */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <span className="text-blue-400 text-lg">💡</span>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">Pro Tips</h3>
                        <div className="mt-2 text-sm text-blue-700 space-y-1">
                            <p>• Start with 1-2 platforms and gradually expand</p>
                            <p>• Each platform has different audience expectations and optimal posting times</p>
                            <p>• Use the test buttons to verify your credentials are working</p>
                            <p>• Set up Discord notifications to monitor your posting activity</p>
                            <p>• LinkedIn supports both personal and company page posting</p>
                        </div>
                    </div>
                </div>
            </div>

            {showLinkedInWizard && (
                <LinkedInOnboardingWizard
                    onComplete={handleLinkedInWizardComplete}
                    onClose={handleLinkedInWizardClose}
                />
            )}
            {/* Meta Platforms Setup Modal */}
            {showMetaSetup && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Meta Platforms Setup</h2>
                            <button
                                onClick={handleMetaWizardClose}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <MetaPlatformsSetup
                            onComplete={handleMetaWizardComplete}
                        />
                    </div>
                </div>
            )}

            {/* Twitter Wizard */}
            {showTwitterWizard && (
                <TwitterOnboardingWizard
                    onComplete={handleTwitterWizardComplete}
                    onClose={handleTwitterWizardClose}
                />
            )}

            {/* Bluesky Wizard */}
            {showBlueskyWizard && (
                <BlueskyOnboardingWizard
                    onComplete={handleBlueskyWizardComplete}
                    onClose={handleBlueskyWizardClose}
                />
            )}

            {showInstagramWizard && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Instagram Setup</h2>
                            <button
                                onClick={handleInstagramWizardClose}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <MetaPlatformsSetup
                            platform="instagram"
                            onComplete={handleInstagramWizardComplete}
                        />
                    </div>
                </div>
            )}

            {showFacebookWizard && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Facebook Setup</h2>
                            <button
                                onClick={handleFacebookWizardClose}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <MetaPlatformsSetup
                            platform="facebook"
                            onComplete={handleFacebookWizardComplete}
                        />
                    </div>
                </div>
            )}

            {showDiscordWizard && (
                <DiscordOnboardingWizard
                    onComplete={handleDiscordWizardComplete}
                    onClose={handleDiscordWizardClose}
                />
            )}

            {/* Dynamic Setup Instructions */}
            {Object.entries(platformsData).some(([_, data]) => data.enabled) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-900 mb-3">Platform-Specific Setup Instructions</h4>
                    <div className="space-y-3">
                        {platformsData.linkedin?.enabled && (
                            <div className="flex items-start space-x-3">
                                <span className="text-lg">💼</span>
                                <div>
                                    <div className="font-medium text-yellow-900">LinkedIn Setup</div>
                                    <div className="text-sm text-yellow-800">
                                        Use the LinkedIn Wizard or manually add tokens in Credentials tab
                                    </div>
                                </div>
                            </div>
                        )}
                        {platformsData.twitter?.enabled && (
                            <div className="flex items-start space-x-3">
                                <span className="text-lg">🐦</span>
                                <div>
                                    <div className="font-medium text-yellow-900">Twitter/X Setup</div>
                                    <div className="text-sm text-yellow-800">
                                        Add API Key, Secret, Access Token, and Access Token Secret in Credentials
                                    </div>
                                </div>
                            </div>
                        )}
                        {platformsData.bluesky?.enabled && (
                            <div className="flex items-start space-x-3">
                                <span className="text-lg">🦋</span>
                                <div>
                                    <div className="font-medium text-yellow-900">BlueSky Setup</div>
                                    <div className="text-sm text-yellow-800">
                                        Add your Bluesky handle and password in Credentials tab
                                    </div>
                                </div>

                            </div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}

export default PlatformsTab;